import { Fragment, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Box, Card, Grid, Switch, Typography, FormControlLabel, Radio, RadioGroup, Alert } from "@mui/material";
import { axios } from "../../../helpers/axios";
import Input from "../../Common/Input";
import Button from "../../Common/Button";
import { format, formatISO, parseISO } from "date-fns";
import { calculateAge, calculateAgeOnDate } from "../../../helpers/utils";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";
import DatePickerComponent from "../../Common/DatePicker";
import BasicSelect from "../../Common/Select";
import { sizeValues } from "../../../helpers/constants";

const FormValidation = yup.object().shape({
    name: yup.string().required("Required"),
    email: yup.string().required("Required"),
    gender: yup.string().required("Required"),
    mobile: yup.string().required("Required"),
    dob: yup.string().required("Required"),
    chss_number: yup.string().required("Required"),
});

export const ParticipantCard = ({ participant, eventStartDate = null }) => {
    return (
        <Grid item xs={12} md={12}>
            <Card
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    mx: "auto",
                    height: { xs: 150, md: 200 },
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {participant?.name} ({participant?.secondary_member_id || participant?.member_id})
                        </Typography>

                        <Box
                            sx={{
                                mt: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">Email: </Typography>
                                <Typography variant="body2">{participant?.email}</Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">Mobile: </Typography>
                                <Typography variant="body2">{participant?.mobile || "Not provided"}</Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">Gender: </Typography>
                                <Typography variant="body2">{participant?.gender || "Not provided"}</Typography>
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2">Age: </Typography>
                                    <Typography variant="body2">
                                        {participant?.dob
                                            ? (eventStartDate 
                                                ? calculateAgeOnDate(new Date(participant?.dob), new Date(eventStartDate))
                                                : calculateAge(new Date(participant?.dob))) || "Not provided"
                                            : "Not provided"}
                                    </Typography>
                                </Box>
                                {eventStartDate && participant?.dob && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0 }}>
                                        Age calculated as on {format(new Date(eventStartDate), "dd MMM yyyy")}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">CHSS Number: </Typography>
                                <Typography variant="body2">{participant?.chss_number || "Not provided"}</Typography>
                            </Box>
                            {(() => {
                                // Check if t-shirt details are missing or show "not updated"
                                const tshirtSize = participant?.tshirt_size?.toString().toLowerCase().trim();
                                const tshirtName = participant?.tshirt_name?.toString().toLowerCase().trim();
                                const clothingType = participant?.clothing_type?.toString().toLowerCase().trim();
                                const clothingSize = participant?.clothing_size?.toString().toLowerCase().trim();
                                
                                const isNotUpdated = 
                                    tshirtSize === "not updated" || 
                                    tshirtSize === "not_updated" ||
                                    tshirtName === "not updated" ||
                                    tshirtName === "not_updated" ||
                                    clothingType === "not updated" ||
                                    clothingType === "not_updated" ||
                                    clothingSize === "not updated" ||
                                    clothingSize === "not_updated";
                                
                                const missingTshirtDetails = participant && (
                                    (!participant.tshirt_size || isNotUpdated) && 
                                    (!participant.tshirt_name || isNotUpdated) && 
                                    (!participant.clothing_type || isNotUpdated) && 
                                    (!participant.clothing_size || isNotUpdated)
                                );
                                
                                return missingTshirtDetails ? (
                                    <Box sx={{ mt: 1, mb: 1 }}>
                                        <Alert severity="warning">
                                            <strong>Note:</strong> This member does not have t-shirt and clothing details in their profile. Please update their profile.
                                        </Alert>
                                    </Box>
                                ) : null;
                            })()}
                            {(() => {
                                // Filter out "not updated" values
                                const tshirtSize = participant?.tshirt_size?.toString().toLowerCase().trim();
                                const tshirtName = participant?.tshirt_name?.toString().toLowerCase().trim();
                                const clothingType = participant?.clothing_type?.toString().toLowerCase().trim();
                                const clothingSize = participant?.clothing_size?.toString().toLowerCase().trim();
                                
                                const hasValidTshirtSize = participant?.tshirt_size && 
                                    tshirtSize !== "not updated" && tshirtSize !== "not_updated" && tshirtSize !== "";
                                const hasValidTshirtName = participant?.tshirt_name && 
                                    tshirtName !== "not updated" && tshirtName !== "not_updated" && tshirtName !== "";
                                const hasValidClothingType = participant?.clothing_type && 
                                    clothingType !== "not updated" && clothingType !== "not_updated" && clothingType !== "";
                                const hasValidClothingSize = participant?.clothing_size && 
                                    clothingSize !== "not updated" && clothingSize !== "not_updated" && clothingSize !== "";
                                
                                const hasClothingDetails = hasValidTshirtSize || hasValidTshirtName || hasValidClothingType || hasValidClothingSize;
                                
                                if (!hasClothingDetails) return null;
                                
                                return (
                                    <>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Clothing Details:</Typography>
                                        </Box>
                                        {hasValidTshirtSize && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2">T-shirt Size: </Typography>
                                                <Typography variant="body2">{participant.tshirt_size}</Typography>
                                            </Box>
                                        )}
                                        {hasValidTshirtName && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2">Name on T-shirt: </Typography>
                                                <Typography variant="body2">{participant.tshirt_name}</Typography>
                                            </Box>
                                        )}
                                        {hasValidClothingType && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2">Clothing Type: </Typography>
                                                <Typography variant="body2">{participant.clothing_type}</Typography>
                                            </Box>
                                        )}
                                        {hasValidClothingSize && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2">{participant.clothing_type} Size: </Typography>
                                                <Typography variant="body2">{participant.clothing_size}</Typography>
                                            </Box>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                    </Box>
                </Box>
            </Card>
        </Grid>
    );
};

export const SingelPlayerDetails = ({
    index,
    player,
    formData,
    setFormData,
    setNonVerifiedMembers,
    nonVerifiedMembers,
    verifiedMembers,
    setVerifiedMembers,
    disabled,
    eventStartDate = null,
}) => {
    const [prefix, setPrefix] = useState("P-");
    const dispatch = useDispatch();
    const formik = useFormik({
        initialValues: {
            secondary_member_id: "",
            member_id: "",
            name: "",
            email: "",
            gender: "",
            mobile: "",
            dob: "",
            chss_number: "",
            is_member: "",
            type: "",
            tshirt_size: "",
            tshirt_name: "",
            clothing_type: "",
            clothing_size: "",
        },
        validationSchema: FormValidation,
        onSubmit: (values, { resetForm }) => {
            const updatedTeam = [...formData.team_members];
            updatedTeam[index] = { ...values, is_member: "No" };

            // check if the player already exists
            const existingPlayer = formData.team_members.find((p) => p.email === values.email);
            if (existingPlayer) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Player already exists in the team",
                        severity: "error",
                    }),
                );
                return;
            }

            setFormData((prev) => ({
                ...prev,
                team_members: updatedTeam,
            }));

            const updatedNonMembers = [...nonVerifiedMembers];
            updatedNonMembers[index] = { ...values, is_member: "No" };
            setNonVerifiedMembers(updatedNonMembers);

            resetForm();
        },
    });

    const handleVerify = async () => {
        try {
            if (formik?.values?.member_id === "") {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please enter membership ID",
                        severity: "error",
                    }),
                );
                return;
            }

            const response = await axios.get(
                `/members/verify?member_id=${
                    prefix === "S" ? "S" + formik?.values?.member_id : formik?.values?.member_id
                }`,
            );
            if (response.success && response.result) {
                const data = {
                    _id: response.result._id,
                    member_id: response.result.member_id,
                    secondary_member_id: response.result.secondary_member_id,
                    name: response.result.name,
                    email: response.result.email,
                    mobile: response.result.mobile,
                    gender: response.result.gender,
                    chss_number: response.result.chss_number,
                    is_member: "Yes",
                    dob: response.result.dob,
                    type: response.result.type,
                    tshirt_size: response.result.tshirt_size,
                    tshirt_name: response.result.tshirt_name,
                    clothing_type: response.result.clothing_type,
                    clothing_size: response.result.clothing_size,
                };

                // check if the same email member already verified
                const existingPlayer = formData.team_members.find((p) => p.member_id === formik?.values?.member_id);
                if (existingPlayer) {
                    dispatch(
                        setSnackBar({
                            open: true,
                            message: "Player already exists in the team",
                            severity: "error",
                        }),
                    );
                    return;
                }

                if (!response.result.dob || response.result.dob === "") {
                    dispatch(
                        setSnackBar({
                            open: true,
                            message: `${response.result.name} does not have DOB in our records`,
                            severity: "error",
                        }),
                    );
                    return;
                }

                if (
                    verifiedMembers.find(
                        (m) => m.member_id === data.member_id && m.secondary_member_id === data.secondary_member_id,
                    )
                ) {
                    dispatch(
                        setSnackBar({
                            open: true,
                            message: "This member is already verified",
                            severity: "error",
                        }),
                    );
                    return;
                }

                setVerifiedMembers((prev) => [...prev, data]);

                const updatedTeam = [...formData.team_members];
                updatedTeam[index] = data;

                setFormData((prev) => ({
                    ...prev,
                    team_members: updatedTeam,
                }));
                dispatch(
                    setSnackBar({
                        open: true,
                        message: `Member verified successfully`,
                        severity: "success",
                    }),
                );
                
                // Check if t-shirt/shorts details are missing
                const missingTshirtDetails = !data.tshirt_size && !data.tshirt_name && !data.clothing_type && !data.clothing_size;
                if (missingTshirtDetails) {
                    dispatch(
                        setSnackBar({
                            open: true,
                            message: `Note: ${data.name} does not have t-shirt and clothing details in their profile. Please update their profile.`,
                            severity: "warning",
                        }),
                    );
                }
            } else {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: response.message || "Verification failed",
                        severity: "error",
                    }),
                );
            }
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || error.message,
                    severity: "error",
                }),
            );
        }
    };

    const currentNonMember = nonVerifiedMembers[index];

    const renderMain = useMemo(() => {
        return (
            <Fragment>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Is the player a member?</Typography>
                    <Switch
                        checked={player?.is_member === "Yes"}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                team_members: prev.team_members.map((tm, i) =>
                                    i === index ? { ...tm, is_member: e.target.checked ? "Yes" : "No" } : tm,
                                ),
                            }));
                        }}
                        disabled={Boolean(
                            player?.name && player?.email && player?.mobile && player?.gender && player?.dob,
                        )}
                    />
                </Box>

                {player.is_member === "Yes" ? (
                    <Fragment>
                        <Grid container spacing={1} alignItems="flex-start">
                            {verifiedMembers.find((m) => m.member_id === player?.member_id) ? (
                                <ParticipantCard participant={player} eventStartDate={eventStartDate} />
                            ) : (
                                <Fragment>
                                    <Grid item xs={12} sm={6}>
                                        <Input
                                            fullWidth
                                            label="Membership ID"
                                            variant="outlined"
                                            size="small"
                                            value={
                                                formik?.values?.secondary_member_id || formik?.values?.member_id || ""
                                            }
                                            prefix={prefix}
                                            prefixOptions={["P-", "S"]}
                                            onPrefixChange={(val) => {
                                                setPrefix(val);
                                                formik.setFieldValue("member_id", ""); // reset ID on prefix change
                                            }}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/^P-/, "");
                                                formik.setFieldValue("member_id", value);
                                            }}
                                            helperText={
                                                prefix === "P-"
                                                    ? "Primary member id should be like P-0001"
                                                    : "Secondary member id should be like S1-0001"
                                            }
                                            placeholder={prefix === "P-" ? "00001" : "1-00001"}
                                        />
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleVerify}
                                            size="small"
                                            sx={{ height: "40px" }}
                                        >
                                            Fetch Details
                                        </Button>
                                    </Grid>
                                </Fragment>
                            )}
                        </Grid>
                    </Fragment>
                ) : currentNonMember ? (
                    <ParticipantCard participant={currentNonMember} eventStartDate={eventStartDate} />
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Input
                                id="name"
                                name="name"
                                label="Name *"
                                onChange={formik.handleChange("name")}
                                value={formik?.values?.name || ""}
                                error={Boolean(formik?.errors.name)}
                                helperText={formik?.errors.name}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Input
                                id="mobile"
                                name="mobile"
                                label="Mobile"
                                onChange={formik?.handleChange("mobile")}
                                value={formik?.values?.mobile || ""}
                                error={Boolean(formik?.errors.mobile)}
                                helperText={formik?.errors.mobile}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Input
                                id="email"
                                name="email"
                                label="Email"
                                onChange={formik?.handleChange("email")}
                                value={formik?.values?.email || ""}
                                error={Boolean(formik?.errors.email)}
                                helperText={formik?.errors.email}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <BasicSelect
                                size="small"
                                value={formik?.values?.gender || ""}
                                onChange={formik?.handleChange("gender")}
                                displayEmpty
                                label="Gender"
                                name="gender"
                                id="gender"
                                items={[
                                    { label: "Male", value: "Male" },
                                    { label: "Female", value: "Female" },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DatePickerComponent
                                id={"dob"}
                                name={"dob"}
                                label="Date of Birth"
                                onChange={(val) => formik?.setFieldValue("dob", formatISO(val))}
                                value={formik?.values?.dob ? new Date(parseISO(formik?.values?.dob)) : null}
                                fullWidth
                                onBlur={formik?.handleBlur}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Input
                                id="chss_number"
                                name="chss_number"
                                label="CHSS / ID Card Number"
                                onChange={formik?.handleChange("chss_number")}
                                value={formik?.values?.chss_number || ""}
                                fullWidth
                            />
                        </Grid>
                        
                        {/* T-shirt and Clothing Details for Non-Members */}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                T-shirt & Clothing Size Chart
                            </Typography>
                            <Box sx={{ 
                                mb: 2, 
                                p: 2, 
                                bgcolor: "#f5f5f5", 
                                borderRadius: 1,
                                minHeight: "auto",
                                display: "block"
                            }}>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.6,
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                        display: "block",
                                        fontSize: "0.875rem"
                                    }}
                                >
                                    <strong>Size Guide:</strong> Please refer to the numerical sizes in parentheses for accurate selection.
                                    <br />
                                    <span style={{ display: "block", marginTop: "4px" }}>
                                        Example: Small (38) means size 38, Medium (40) means size 40, etc.
                                    </span>
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <BasicSelect
                                size="small"
                                value={formik?.values?.tshirt_size || ""}
                                onChange={formik.handleChange("tshirt_size")}
                                displayEmpty
                                label="T-shirt Size"
                                name="tshirt_size"
                                id="tshirt_size"
                                items={sizeValues.map((e) => ({
                                    label: e,
                                    value: e,
                                }))}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Input
                                id="tshirt_name"
                                name="tshirt_name"
                                label="Name on Tshirt"
                                onChange={formik.handleChange("tshirt_name")}
                                value={formik?.values?.tshirt_name || ""}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Clothing Type
                            </Typography>
                            <RadioGroup
                                row
                                value={formik?.values?.clothing_type || ""}
                                onChange={(e) => {
                                    formik.setFieldValue("clothing_type", e.target.value);
                                    formik.setFieldValue("clothing_size", ""); // Reset size on type change
                                }}
                            >
                                <FormControlLabel value="Shorts" control={<Radio />} label="Shorts" />
                                <FormControlLabel value="Track Pants" control={<Radio />} label="Track Pants" />
                            </RadioGroup>
                        </Grid>
                        {formik?.values?.clothing_type && (
                            <Grid item xs={12} md={6}>
                                <BasicSelect
                                    size="small"
                                    value={formik?.values?.clothing_size || ""}
                                    onChange={formik.handleChange("clothing_size")}
                                    displayEmpty
                                    label={`${formik?.values?.clothing_type} Size`}
                                    name="clothing_size"
                                    id="clothing_size"
                                    items={sizeValues.map((e) => ({
                                        label: e,
                                        value: e,
                                    }))}
                                    fullWidth
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={12} display={"flex"} justifyContent={"flex-end"} gap={1}>
                            <Button type="submit" loading={formik.isSubmitting} onClick={formik.handleSubmit}>
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Fragment>
        );
    }, [player, formik.values, verifiedMembers, nonVerifiedMembers, currentNonMember, prefix, handleVerify]);

    return renderMain;
};
